import React, { useEffect, useState } from "react";
import "./Inundation.css";
import L from 'leaflet';
import { MapContainer, TileLayer, LayersControl, GeoJSON, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { MapHomeControl, MapInfoControl } from "../../../common/MapControls/MapControls";

const RIVER_BOUNDS = {
    comoro: [
        [-8.603431459847762, 125.4742956084296],
        [-8.531630078534542, 125.61714466124778],
    ],
    northlaklo: [
        [-8.629157621382236, 125.76072467125304],
        [-8.41385376317635, 126.11830085934785],
    ],
};

// WMS Layer Component
const WMSLayer = ({ onLayerLoaded, returnYear, river }) => {
    const map = useMap();

    useEffect(() => {
        const riverLayerPrefix = river === "northlaklo" ? "northlaklo" : "comoro";
        const wmsLayer = L.tileLayer.wms('https://drr-dss.rimes.int/flood_inun/', {
            layers: `flood_section:${riverLayerPrefix}_${returnYear}year`,
            styles: 'flood_section:flood_inundation_v1',
            format: 'image/png',
            transparent: true,
            version: '1.1.0',
            attribution: 'Flood Inundation Data',
            maxZoom: 22,
        });

        wmsLayer.addTo(map);

        let hasHandledLoad = false;
        const selectedBounds = RIVER_BOUNDS[riverLayerPrefix] || RIVER_BOUNDS.comoro;
        const handleLayerReady = () => {
            if (hasHandledLoad) return;
            hasHandledLoad = true;

            map.fitBounds(selectedBounds, {
                padding: [20, 20],
                animate: true,
            });

            if (onLayerLoaded) onLayerLoaded();
        };

        wmsLayer.once("load", handleLayerReady);

        const timer = setTimeout(() => {
            handleLayerReady();
        }, 1500);

        return () => {
            clearTimeout(timer);
            wmsLayer.off("load", handleLayerReady);
            map.removeLayer(wmsLayer);
        };
    }, [map, onLayerLoaded, returnYear, river]);

    return null;
};

// Legend Component
const FloodLegend = () => {
    return (
        <div className="flood-legend-container" style={{
            position: 'absolute',
            bottom: '20px',
            right: '20px',
            backgroundColor: 'white',
            padding: '10px',
            borderRadius: '8px',
            boxShadow: '0 0 15px rgba(0,0,0,0.2)',
            zIndex: 999,
            maxWidth: '250px'
        }}>
            <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>Flood Depth Scale</h4>
            <img 
                src="/Flood/depth_scale_v1.png"
                alt="Flood Depth Scale Legend" 
                style={{ width: '50%', height: 'auto' }}
            />
        </div>
    );
};

const RETURN_YEAR_OPTIONS = [2, 5, 10, 25, 50, 100, 500, 1000, 10000];

const FilterSection = ({
    selectedReturnYear,
    setSelectedReturnYear,
    selectedRiver,
    setSelectedRiver,
}) => (
    <div style={{
        padding: "12px 16px",
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
        border: "1px solid #e8ecf1",
        marginBottom: "12px",
    }}>
        <div style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "12px",
        }}>
            {/* Filter Header */}
            <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 22 }} />
                <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>Filter</span>
            </div>

            {/* Divider */}
            <div style={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

         

            {/* River Dropdown */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label htmlFor="river-select" style={{ fontWeight: 600, fontSize: "13px", color: "#333", whiteSpace: "nowrap" }}>
                    River:
                </label>
                <select
                    id="river-select"
                    value={selectedRiver}
                    onChange={(e) => setSelectedRiver(e.target.value)}
                    style={{ padding: "6px 10px", fontSize: "13px", borderRadius: "6px", border: "1px solid #ccc", minWidth: "180px" }}
                >
                    <option value="comoro">Comoro</option>
                    <option value="northlaklo">North Laklo</option>
                </select>
            </div>

               {/* Return Year Period Dropdown */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <label htmlFor="return-year-select" style={{ fontWeight: 600, fontSize: "13px", color: "#333", whiteSpace: "nowrap" }}>
                    Return Year Period:
                </label>
                <select
                    id="return-year-select"
                    value={selectedReturnYear}
                    onChange={(e) => setSelectedReturnYear(Number(e.target.value))}
                    style={{ padding: "6px 10px", fontSize: "13px", borderRadius: "6px", border: "1px solid #ccc", minWidth: "180px" }}
                >
                    {RETURN_YEAR_OPTIONS.map((year) => (
                        <option key={year} value={year}>{year} Year Return Period</option>
                    ))}
                </select>
            </div>
        </div>
    </div>
);

const HydroForecast = () => {
    const URLTILEMAP_WHITE = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";

    const [boundaryData, setBoundaryData] = useState(null);
    const [rnTl, setrnTl] = useState(null);
    const [selectedReturnYear, setSelectedReturnYear] = useState(2);
    const [selectedRiver, setSelectedRiver] = useState("comoro");

    // Load boundary & river network
    useEffect(() => {
        fetch("/DATA/OSMbased_adm1_boundary.json").then(res => res.json()).then(setBoundaryData);
        fetch("/DATA/Flood/tl_rn_final.geojson").then(res => res.json()).then(setrnTl);
    }, []);

    return (
        <div className="map-containers">
            <FilterSection
                selectedReturnYear={selectedReturnYear}
                setSelectedReturnYear={setSelectedReturnYear}
                selectedRiver={selectedRiver}
                setSelectedRiver={setSelectedRiver}
            />
            {/* Map */}
            <MapContainer center={[-8.7950, 125.6877]} zoom={9} maxZoom={22} zoomControl={true} attributionControl={false} style={{ height: 550, width: "100%", borderRadius: 12 }}>
              <MapHomeControl />
              <MapInfoControl text="Modelled flood inundation extents. Use the controls to switch scenarios and return periods." />
                <LayersControl position="topright">
                    <LayersControl.BaseLayer name="Inundation Layer" checked>
                        <TileLayer url={URLTILEMAP_WHITE} />
                    </LayersControl.BaseLayer>
                    
                    <LayersControl.Overlay name="Flood Inundation" checked>
                        <WMSLayer returnYear={selectedReturnYear} river={selectedRiver} />
                    </LayersControl.Overlay>

                    <LayersControl.Overlay name="Timor-Leste">
                        {boundaryData && <GeoJSON data={boundaryData} interactive={false} style={{ color: "#ff0000", weight: 1, fillOpacity: 0 }} />}
                    </LayersControl.Overlay>

                    <LayersControl.Overlay name="River Networks">
                        {rnTl && <GeoJSON data={rnTl} interactive={false} style={{ color: "#0000ff", weight: 1, fillOpacity: 0 }} />}
                    </LayersControl.Overlay>
                </LayersControl>
                
<FloodLegend />
            </MapContainer>
        </div>
    );
};

export default HydroForecast;